import { Button, CText } from '@bm-js/atlascloud-ui'
import { useTr } from '../../utils/useTr'
import { TrKey } from '../../cnst/translations/trKey.cnst'
import { useCmsEditorActions, useCmsEditorData } from '../../utils/hooks'
import { useCallback, useState } from 'react'
import { CmsModel, CmsModelEntry, CmsModelType } from '@bm-js/atlascloud-shared'
import { v4 as uuidv4 } from 'uuid'
import LinkButton from '../LinkButton'
import { getEntryTitle } from '../../utils/cms.util'

const CmsEditorLeft = () => {
  const { tr } = useTr()
  const editorData = useCmsEditorData()
  const { openEntry, patchConfig } = useCmsEditorActions()
  const [activeModel, setActiveModel] = useState<CmsModel | null>(null)

  const selectCollectionModel = useCallback(
    (model: CmsModel) => {
      setActiveModel(model)
    },
    [setActiveModel]
  )

  const createEmptyEntry = (model: CmsModel): CmsModelEntry => {
    return {
      uuid: uuidv4(),
      fields: model.fields.map((modelField) => ({
        type: modelField.type,
        key: modelField.key,
        uuid: uuidv4(),
        value: undefined,
      })),
    }
  }

  const selectSingletonModel = useCallback(
    (model: CmsModel) => {
      const firstEntry = editorData.workingConfig.entities[model.key].entries[0]
      if (firstEntry) return openEntry(model, firstEntry)

      const newEntry = createEmptyEntry(model)
      const updatedEntities = { ...editorData.workingConfig.entities }
      updatedEntities[model.key].entries.push(newEntry)
      patchConfig({ entities: updatedEntities })
      openEntry(model, newEntry)
    },
    [openEntry, editorData.workingConfig.entities, patchConfig]
  )

  const selectModel = useCallback(
    (model: CmsModel) => {
      const { type } = model
      if (type === CmsModelType.SINGLETON) return selectSingletonModel(model)
      if (type === CmsModelType.COLLECTION) return selectCollectionModel(model)
    },
    [selectSingletonModel, selectCollectionModel]
  )

  const addNewEntry = () => {
    if (!activeModel) return

    const newEntry = createEmptyEntry(activeModel)
    const updatedEnities = { ...editorData.workingConfig.entities }
    updatedEnities[activeModel.key].entries.push(newEntry)
    patchConfig({ entities: updatedEnities })
  }

  return (
    <div className='h-full overflow-y-auto border-r border-gray-300 bg-white'>
      {activeModel ? (
        <>
          <div className='p-2'>
            <CText tag='h2' className='text-primary-400 text-lg font-bold'>
              {activeModel.labelPlural}
            </CText>
            <LinkButton
              onClick={() => setActiveModel(null)}
              className='underline'
            >
              {'<-'} Tillbaka till modeller
            </LinkButton>
            <Button className='w-full mt-2 mb-4' onClick={addNewEntry}>
              Skapa ny {activeModel.label.toLowerCase()}
            </Button>
          </div>

          {editorData.workingConfig.entities[activeModel.key].entries.map(
            (entry) => (
              <button
                key={entry.uuid}
                className='block w-full text-left p-2 border-t last:border-b border-gray-300 hover:bg-gray-200 cursor-pointer'
                onClick={() => openEntry(activeModel, entry)}
              >
                {getEntryTitle(activeModel, entry, tr)}
              </button>
            )
          )}
        </>
      ) : (
        <>
          <CText tag='h2' className='text-primary-400 text-lg font-bold p-2'>
            {tr(TrKey.cmsEditorLeftHeader)}
          </CText>
          {editorData.backendResponse.website.cmsModels.map((model) => (
            <button
              key={model.uuid}
              onClick={() => selectModel(model)}
              className='block w-full text-left p-2 border-t last:border-b border-gray-300 hover:bg-gray-200 cursor-pointer'
            >
              <CText>{model.labelPlural || model.label}</CText>
            </button>
          ))}
        </>
      )}
    </div>
  )
}

export default CmsEditorLeft
